import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { dashboardUpdateBusinessNotesRequest } from '@/modules/dashboard/dashboardActions';
import {
	EmailContactIcon,
	Messenger,
	PhoneContactIcon,
	PlusGold,
	DropDownGold,
	Website,
	Google,
	Trash,
	Yelp,
	Instagram,
	YelpIcon,
	YelpStar,
	YelpStarEmpty,
	HardArrowLeft
} from '@/assets/images';
import encodeUrl from '@/utils/encodeUrl';
import { sendAnalytics } from '@/services/analyticsService';
import { UserRole, type User } from '@/modules/user/userTypes';
import type { RootState } from '@/rootReducer';
import Button from '@/components/Button/newButtons';
import type { Email } from '@/modules/email/emailTypes';
import { useFreeTrialModal } from '@/modules/freeTrial/FreeTrialModalContext';
import type { Business, ContactType, UserNotes } from '../../dashboardTypes';
import './BusinessPreviewEmailModal.scss';

interface Props {
	user: User;
	setDeleteDataIndex?: (x: number) => void;
	to: string;
	setTo: (x: string) => void;
	business: Business | undefined;
	editedEmails: { [x: string]: Email };
	setEditedEmails?: (x: { [x: string]: Email }) => void;
	editedEmailsTo?: { [x: string]: string };
	setEditedEmailsTo?: (x: { [x: string]: string }) => void;
	noEdit?: boolean;
	isVisible?: boolean;
	setIsVisible?: (x: boolean) => void;
}

const BusinessPreviewEmailModal = ({
	user,
	setDeleteDataIndex,
	to,
	setTo,
	business,
	editedEmails,
	setEditedEmails,
	editedEmailsTo,
	setEditedEmailsTo,
	noEdit = false,
	setIsVisible
}: Props): JSX.Element => {
	const [scheduledEmailActivity] = useSelector(
		(state: RootState) => [
			state.email.getIn(['email', 'scheduled', 'activity']).toJS()
		],
		shallowEqual
	);

	const [showAboutMore, setShowAboutMore] = useState(false);
	const { openFreeTrialModal } = useFreeTrialModal();

	const handleOpenFreeTrialModal = () => {
		openFreeTrialModal();
	};

	const dispatch = useDispatch();
	const setBusinessNotes = (businessId: string, notes: UserNotes) =>
		dispatch(dashboardUpdateBusinessNotesRequest({ businessId, notes }));

	const toggleAbout = () => {
		if (showAboutMore) {
			setShowAboutMore(false);
		} else {
			setShowAboutMore(true);
		}
	};

	const toggleContacted = (type: string, link = false) => {
		if (!business) {
			return;
		}

		let contactType: ContactType;

		if (business.contactType && link) {
			contactType = {
				...business.contactType,
				...business.lead,
				[type]: true
			};
		} else if (business.contactType) {
			contactType = {
				...business.contactType,
				...business.lead,
				[type]: !(
					business.contactType[type as keyof ContactType] ?? false
				)
			};
		} else {
			contactType = {
				[type]: true
			};
		}

		let businessIsContacted: boolean | undefined;
		let userAddedContact = false;
		if (business.userAddedData) {
			business.userAddedData.forEach(data => {
				if (data.checked) {
					userAddedContact = true;
				}
			});
		}

		if (
			[
				'messenger',
				'phone',
				'emailPrimary',
				'emailSecondary',
				'thirdEmail',
				'instagramMessenger'
			].includes(type)
		) {
			businessIsContacted =
				contactType?.messenger ||
				contactType?.phone ||
				contactType?.emailPrimary ||
				contactType?.emailSecondary ||
				contactType?.thirdEmail ||
				contactType?.instagramMessenger ||
				userAddedContact;
		} else {
			businessIsContacted = business.isContacted;
		}

		if (setBusinessNotes) {
			const businessId = business._id || business.id;
			const currentNoteEl = document.getElementsByClassName(
				'business-notes--text'
			)[0];
			let currentNoteVal;
			if (currentNoteEl) {
				currentNoteVal = currentNoteEl.value;
			}
			setBusinessNotes(
				businessId as string,
				{
					isContacted: businessIsContacted,
					lead: business?.lead,
					note: business?.note ?? '',
					currentNoteText: currentNoteVal,
					contactType,
					userAddedData: business.userAddedData
				} as UserNotes
			);
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `User contacted a business via their: ${type.toUpperCase()}`,
					properties: {
						origin: 'Business side panel in the email modal',
						businessName: business.name,
						businessId: business._id || business.id,
						businessAddress: business.address
					}
				});
			}
		}
	};

	const toggleContactedUserData = (index: number) => {
		business.userAddedData[index].checked =
			!business.userAddedData[index].checked;
		let contacted = false;
		if (business.userAddedData) {
			business.userAddedData.forEach(data => {
				if (data.checked) {
					contacted = true;
				}
			});
		}
		if (business.contactType) {
			const contactKeys = Object.keys(business.contactType);
			if (contactKeys.length > 0) {
				contactKeys.forEach(key => {
					if (business.contactType[key]) {
						contacted = true;
					}
				});
			}
		}
		const note = {
			isContacted: contacted,
			lead: business.lead,
			note: business.note ?? '',
			contactType: business.contactType,
			userAddedData: business.userAddedData ?? ''
		};

		const businessId = business._id || business.id;

		setBusinessNotes(businessId as string, note as UserNotes);
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User contacted a business via their User Added Data`,
				properties: {
					origin: 'Business side panel in the email modal',
					address: business?.address,
					id: business?._id,
					name: business?.name
				}
			});
		}
	};

	const openAddDataModal = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.add('modal-exists');
		const zChangeEl = modalEl.parentNode.parentNode.querySelector(
			'.dashboard-container--resizable-wrapper'
		);
		if (zChangeEl) {
			zChangeEl.style.zIndex = 1;
		}
		setTimeout(() => {
			modalEl.classList.add('modal-visible');
		});
	};

	const openHoursDropdown = () => {
		const hoursEl = document.getElementsByClassName(
			'hours-holder-email-modal'
		)[0];
		const hoursChev = document.getElementsByClassName(
			'down-chevron-email-modal'
		)[0];
		if (!hoursEl.classList.contains('hours-opened')) {
			hoursEl.classList.add('hours-opened');
			hoursChev.classList.add('flipped');
		}
	};

	const closeHoursDropdown = () => {
		const hoursEl = document.getElementsByClassName(
			'hours-holder-email-modal'
		)[0];
		const hoursChev = document.getElementsByClassName(
			'down-chevron-email-modal'
		)[0];
		if (hoursEl && hoursEl.classList.contains('hours-opened')) {
			hoursEl.classList.remove('hours-opened');
			hoursChev.classList.remove('flipped');
		}
	};

	const toggleHoursDropdown = () => {
		const hoursEl = document.getElementsByClassName(
			'hours-holder-email-modal'
		)[0];
		if (hoursEl && hoursEl.classList.contains('hours-opened')) {
			closeHoursDropdown();
		} else {
			openHoursDropdown();
		}
	};

	useEffect(() => {
		closeHoursDropdown();

		if (showAboutMore) {
			setShowAboutMore(false);
		}
	}, [business?._id]);

	const addLinkAnalytics = (type: string) => {
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User contacted a business via their: ${type}`,
			properties: {
				origin: 'Business side panel in the email modal',
				businessName: business?.name,
				businessId: business?.id || business?._id,
				businessAddress: business?.address
			}
		});
	};

	const searchTerm = () => {
		const term = `${business?.name} ${business?.address?.city} ${business?.address?.state}`;
		return term.replace('&', '%26');
	};

	const openDeleteDataModal = (index: number) => {
		const e = document.getElementById('delete-data-modal');
		e?.classList.remove('delete-data-modal-fade');
		setTimeout(() => {
			e?.classList.remove('delete-data-modal-closed');
		}, 500);

		setDeleteDataIndex?.(index);
	};

	const instagramRegex = instagramUrl => {
		const igRegex =
			/^https?:\/\/(?:www\.)?instagram\.com\/([A-Za-z0-9._]+)/;
		const match = instagramUrl.match(igRegex);

		if (match && match[1]) {
			const username = match[1];
			const directUrl = `https://ig.me/m/${username}`;
			return directUrl;
		}
		return instagramUrl;
	};

	const isUserFreeTier =
		user.role === UserRole.Free || user.role === UserRole.PreTrial;

	return (
		<div className="business-preview-email-modal grid h-full grid-rows-[auto_auto_1fr] overflow-hidden lg:block lg:h-auto">
			<div className="sticky left-0 top-0 lg:hidden">
				<Button
					onClick={() => {
						if (setIsVisible) {
							setIsVisible(false);
						}
					}}
					variant="text"
					size="large"
					leftIcon={<HardArrowLeft />}
					className="m-2 px-0"
				>
					Return
				</Button>
			</div>

			<div className="business-preview-email-modal-header">
				<div className="business-preview-email-modal-header-title">
					Business Information
				</div>
				<div className="business-preview-email-modal-header-business-name">
					{business?.name}
				</div>
			</div>
			<div className="business-preview-email-modal-inner lg:overflow-[unset] overflow-auto">
				<div className="business-preview-email-modal-contact-title">
					Contact
				</div>
				<div
					className="business-preview-email-modal-contact group data-[freetier=true]:select-none data-[freetier=true]:blur-sm"
					data-freetier={isUserFreeTier}
				>
					{business &&
						business.email &&
						business.emailLowPriority !== true && (
							<div
								className={`business-preview-email-modal-contact-email ${
									business.email.toLowerCase() ===
									to.toLowerCase()
										? 'highlight'
										: ''
								} ${
									scheduledEmailActivity[`${business._id}`] &&
									!noEdit
										? 'disabled'
										: ''
								} `}
								onClick={e => {
									if (isUserFreeTier) {
										e.preventDefault();
										handleOpenFreeTrialModal();

										return;
									}
									if (
										scheduledEmailActivity[
											`${business._id}`
										]
									) {
										return;
									}

									if (
										business.email.toLowerCase() !==
										to.toLowerCase()
									) {
										setTo(business.email.toLowerCase());

										if (noEdit) return;

										if (setEditedEmails) {
											setEditedEmails({
												...editedEmails,
												[business?._id]: {
													...editedEmails[
														`${business?._id}`
													],
													email: business.email.toLowerCase()
												}
											});
										} else {
											setEditedEmailsTo({
												...editedEmailsTo,
												[business?._id]:
													business.email.toLowerCase()
											});
										}
									}
								}}
							>
								<EmailContactIcon className="business-preview-email-modal-contact-email-icon" />
								<span
									className={`${user.role === UserRole.Free || user.role === UserRole.PreTrial ? 'blur-sm' : ''}`}
								>
									{business.email}
								</span>
							</div>
						)}
					{business?.facebook?.email &&
						business.facebook.emailLowPriority !== true &&
						business.facebook.email !== business.email && (
							<div
								className={`business-preview-email-modal-contact-email ${
									business.facebook.email.toLowerCase() ===
									to.toLowerCase()
										? 'highlight'
										: ''
								} ${
									scheduledEmailActivity[`${business._id}`] &&
									!noEdit
										? 'disabled'
										: ''
								} `}
								onClick={e => {
									if (isUserFreeTier) {
										e.preventDefault();
										handleOpenFreeTrialModal();
										return;
									}
									if (
										scheduledEmailActivity[
											`${business._id}`
										]
									) {
										return;
									}

									if (
										business.facebook.email.toLowerCase() !==
										to.toLowerCase()
									) {
										setTo(
											business.facebook.email.toLowerCase()
										);

										if (noEdit) return;

										if (setEditedEmails) {
											setEditedEmails({
												...editedEmails,
												[business?._id]: {
													...editedEmails[
														`${business?._id}`
													],
													email: business.facebook.email.toLowerCase()
												}
											});
										} else {
											setEditedEmailsTo({
												...editedEmailsTo,
												[business?._id]:
													business.facebook.email.toLowerCase()
											});
										}
									}
								}}
							>
								<EmailContactIcon className="business-preview-email-modal-contact-email-icon" />
								<span
									className={`${user.role === UserRole.Free || user.role === UserRole.PreTrial ? 'blur-sm' : ''}`}
								>
									{business.facebook.email}
								</span>
							</div>
						)}
					{business?.web?.email &&
						business.web.email !== business.email &&
						business.web.emailLowPriority !== true &&
						business.web.email !== business.facebook?.email && (
							<div
								className={`business-preview-email-modal-contact-email ${
									business.web.email.toLowerCase() ===
									to.toLowerCase()
										? 'highlight'
										: ''
								} ${
									scheduledEmailActivity[`${business._id}`] &&
									!noEdit
										? 'disabled'
										: ''
								} `}
								onClick={e => {
									if (isUserFreeTier) {
										e.preventDefault();
										handleOpenFreeTrialModal();
										return;
									}
									if (
										scheduledEmailActivity[
											`${business._id}`
										]
									) {
										return;
									}

									if (
										business.web.email.toLowerCase() !==
										to.toLowerCase()
									) {
										setTo(business.web.email.toLowerCase());

										if (noEdit) return;

										if (setEditedEmails) {
											setEditedEmails({
												...editedEmails,
												[business?._id]: {
													...editedEmails[
														`${business?._id}`
													],
													email: business.web.email.toLowerCase()
												}
											});
										} else {
											setEditedEmailsTo({
												...editedEmailsTo,
												[business?._id]:
													business.web.email.toLowerCase()
											});
										}
									}
								}}
							>
								<EmailContactIcon className="business-preview-email-modal-contact-email-icon" />
								<span
									className={`${user.role === UserRole.Free || user.role === UserRole.PreTrial ? 'blur-sm' : ''}`}
								>
									{business.web.email}
								</span>
							</div>
						)}
					{business?.userAddedData?.map((data, ind) => {
						if (data.type === 'email') {
							return (
								<div
									className="business-preview-email-modal-contact-user-added-email"
									key={Math.random()}
								>
									<div
										className={`business-preview-email-modal-contact-email ${
											data.data.toLowerCase() ===
											to.toLowerCase()
												? 'highlight'
												: ''
										} ${
											scheduledEmailActivity[
												`${business._id}`
											] && !noEdit
												? 'disabled'
												: ''
										} `}
										onClick={e => {
											if (isUserFreeTier) {
												e.preventDefault();
												handleOpenFreeTrialModal();
												return;
											}
											if (
												scheduledEmailActivity[
													`${business._id}`
												]
											) {
												return;
											}

											if (
												data.data.toLowerCase() !==
												to.toLowerCase()
											) {
												setTo(data.data.toLowerCase());

												if (noEdit) return;

												if (setEditedEmails) {
													setEditedEmails({
														...editedEmails,
														[business?._id]: {
															...editedEmails[
																`${business?._id}`
															],
															email: data.data.toLowerCase()
														}
													});
												} else {
													setEditedEmailsTo({
														...editedEmailsTo,
														[business?._id]:
															data.data.toLowerCase()
													});
												}
											}
										}}
									>
										<EmailContactIcon className="business-preview-email-modal-contact-email-icon" />
										<span>{data.data}</span>
									</div>
									{!noEdit && (
										<Trash
											className="business-preview-email-modal-trash-icon"
											onClick={() => {
												openDeleteDataModal(ind);
											}}
										/>
									)}
								</div>
							);
						}
					})}
					{business?.facebook?.messengerUrl && (
						<a
							rel="noreferrer"
							className="business-preview-email-modal-contact-facebook"
							href={encodeUrl(
								`https://${business.facebook.messengerUrl}`
							)}
							onDragStart={e => {
								if (isUserFreeTier) {
									e.preventDefault();
								}
							}}
							target="_blank"
							onClick={e => {
								if (isUserFreeTier) {
									e.preventDefault();
									handleOpenFreeTrialModal();
									return;
								}
								toggleContacted('messenger', true);
							}}
						>
							<Messenger className="business-preview-email-modal-contact-facebook-icon" />
							Facebook Messenger
						</a>
					)}
					{business?.socials?.instagram && (
						<a
							rel="noreferrer"
							className="business-preview-email-modal-contact-facebook"
							href={encodeUrl(
								instagramRegex(business?.socials?.instagram)
							)}
							onDragStart={e => {
								if (isUserFreeTier) {
									e.preventDefault();
								}
							}}
							target="_blank"
							onClick={e => {
								if (isUserFreeTier) {
									e.preventDefault();
									handleOpenFreeTrialModal();
									return;
								}
								toggleContacted('messenger', true);
							}}
						>
							<Messenger className="business-preview-email-modal-contact-facebook-icon" />
							Instagram Messenger
						</a>
					)}
					{business?.phone?.phone && (
						<a
							rel="noreferrer"
							className="business-preview-email-modal-contact-phone"
							href={`tel:${business.phone.display}`}
							onDragStart={e => {
								if (isUserFreeTier) {
									e.preventDefault();
								}
							}}
							target="_blank"
							onClick={e => {
								if (isUserFreeTier) {
									e.preventDefault();
									handleOpenFreeTrialModal();
									return;
								}
								toggleContacted('phone');
							}}
						>
							<PhoneContactIcon className="business-preview-email-modal-contact-phone-icon" />
							{business.phone.display}
						</a>
					)}
					{business?.userAddedData?.map((data, ind) => {
						if (data.type === 'phone') {
							return (
								<div
									className="business-preview-email-modal-contact-user-added-phone"
									key={Math.random()}
								>
									<a
										rel="noreferrer"
										className="business-preview-email-modal-contact-phone"
										href={`tel:${data.data}`}
										onDragStart={e => {
											if (isUserFreeTier) {
												e.preventDefault();
											}
										}}
										target="_blank"
										onClick={e => {
											if (isUserFreeTier) {
												e.preventDefault();
												handleOpenFreeTrialModal();
												return;
											}
											toggleContactedUserData(ind);
										}}
									>
										<PhoneContactIcon className="business-preview-email-modal-contact-phone-icon" />
										{data.data}
									</a>
									{!noEdit && (
										<Trash
											className="business-preview-email-modal-trash-icon"
											onClick={e => {
												if (isUserFreeTier) {
													e.preventDefault();
													handleOpenFreeTrialModal();
													return;
												}
												openDeleteDataModal(ind);
											}}
										/>
									)}
								</div>
							);
						}
					})}
					{!noEdit && (
						<div
							className="business-preview-email-modal-contact-add"
							onClick={e => {
								if (isUserFreeTier) {
									e.preventDefault();
									handleOpenFreeTrialModal();
									return;
								}
								openAddDataModal;
							}}
						>
							<div className="business-preview-email-modal-contact-divider" />
							<PlusGold />
							Add Contact Data
						</div>
					)}
				</div>
				<div className="business-preview-email-modal-yelp">
					{business?.yelp?.reviews ? (
						<>
							<div className="business-preview-email-modal-yelp-stars-holder">
								<YelpIcon className="business-preview-email-modal-yelp-logo" />
								{!business?.yelp?.rating ||
								business.yelp.rating === 0 ? (
									<div className="yelp-no-rating">
										No Yelp reviews
									</div>
								) : (
									<>
										{business?.yelp?.rating > 0 ? (
											<YelpStar className="business-preview-email-modal-yelp-star" />
										) : (
											<YelpStarEmpty className="business-preview-email-modal-yelp-star" />
										)}
										{business?.yelp?.rating > 1 ? (
											<YelpStar className="business-preview-email-modal-yelp-star" />
										) : (
											<YelpStarEmpty className="business-preview-email-modal-yelp-star" />
										)}
										{business?.yelp?.rating > 2 ? (
											<YelpStar className="business-preview-email-modal-yelp-star" />
										) : (
											<YelpStarEmpty className="business-preview-email-modal-yelp-star" />
										)}
										{business?.yelp?.rating > 3 ? (
											<YelpStar className="business-preview-email-modal-yelp-star" />
										) : (
											<YelpStarEmpty className="business-preview-email-modal-yelp-star" />
										)}
										{business?.yelp?.rating > 4 ? (
											<YelpStar className="business-preview-email-modal-yelp-star" />
										) : (
											<YelpStarEmpty className="business-preview-email-modal-yelp-star" />
										)}
									</>
								)}
							</div>
							{business?.yelp?.rating > 0 ? (
								<div className="business-preview-email-modal-yelp-review-count">
									{business.yelp.reviews} Review
									{business.yelp.reviews === 0 ? '' : 's'}
								</div>
							) : (
								''
							)}
						</>
					) : (
						<div className="business-preview-email-modal-yelp-no-reviews">
							<YelpIcon className="business-preview-email-modal-yelp-logo" />
							<div className="yelp-no-rating">
								No Yelp reviews
							</div>
						</div>
					)}
				</div>
				{business?.address?.address && (
					<div className="business-preview-email-modal-address">
						<div className="business-preview-email-modal-address-title">
							Address
						</div>
						{business.address.address}
					</div>
				)}
				{!business?.hours ||
				(!business.hours.M &&
					!business.hours.T &&
					!business.hours.W &&
					!business.hours.Th &&
					!business.hours.F &&
					!business.hours.S &&
					!business.hours.Su) ? (
					''
				) : (
					<>
						<div
							className="info-tab-address-open"
							onClick={toggleHoursDropdown}
						>
							<span>See all location hours</span>{' '}
							<DropDownGold className="down-chevron down-chevron-email-modal" />
						</div>
						<div className="hours-holder hours-holder-email-modal">
							{business.hours.M ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">Monday</span>{' '}
									<span className="hours-text">
										{business.hours.M}
									</span>
								</div>
							) : (
								''
							)}
							{business.hours.T ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">Tuesday</span>{' '}
									<span className="hours-text">
										{business.hours.T}
									</span>
								</div>
							) : (
								''
							)}
							{business.hours.W ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">
										Wednesday
									</span>{' '}
									<span className="hours-text">
										{business.hours.W}
									</span>
								</div>
							) : (
								''
							)}
							{business.hours.Th ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">
										Thursday
									</span>{' '}
									<span className="hours-text">
										{business.hours.Th}
									</span>
								</div>
							) : (
								''
							)}
							{business.hours.F ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">Friday</span>{' '}
									<span className="hours-text">
										{business.hours.F}
									</span>
								</div>
							) : (
								''
							)}
							{business.hours.S ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">
										Saturday
									</span>{' '}
									<span className="hours-text">
										{business.hours.S}
									</span>
								</div>
							) : (
								''
							)}
							{business.hours.Sa ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">
										Saturday
									</span>{' '}
									<span className="hours-text">
										{business.hours.Sa}
									</span>
								</div>
							) : (
								''
							)}
							{business.hours.Su ? (
								<div className="info-tab-address-hours">
									<span className="hours-label">Sunday</span>{' '}
									<span className="hours-text">
										{business.hours.Su}
									</span>
								</div>
							) : (
								''
							)}
						</div>
					</>
				)}
				{business?.yelp?.about && (
					<div className="business-preview-email-modal-about">
						<div className="info-tab-section-title">About</div>
						<>
							<div
								className={`info-tab-section-about ${
									showAboutMore ? 'info-tab-about-full' : ''
								}`}
							>
								{business.yelp.about}
							</div>
							{business.yelp.about.length > 115 ? (
								<span
									className="info-tab-about-more"
									onClick={toggleAbout}
								>
									{!showAboutMore
										? '...show more'
										: 'show less'}
								</span>
							) : (
								''
							)}
						</>
					</div>
				)}
				<div className="business-preview-email-modal-categories">
					{business?.categories?.search && (
						<>
							<div className="info-tab-section-title">
								Categories
							</div>
							{business.categories.search.map(category => {
								return (
									<div
										className="single-category-pill"
										key={category}
									>
										{category}
									</div>
								);
							})}
						</>
					)}
				</div>
				<div className="info-tab-online-presence-holder">
					<div className="info-tab-section-title">
						Online Presence
					</div>
					{business?.website &&
						business.website !== 'false' &&
						business.website !== false && (
							<div className="business-preview-email-modal-link-row">
								<a
									rel="noreferrer"
									href={encodeUrl(
										business.website.startsWith(
											'http://'
										) ||
											business.website.startsWith(
												'https://'
											)
											? business.website
											: `http://${business.website}`
									)}
									className="business-preview-email-modal-link"
									target="_blank"
									onClick={() => {
										addLinkAnalytics('WEBSITE');
										setBusinessNotes(
											business.id ||
												(business._id as string),
											{
												contactType: {
													website: true
												},
												isContacted:
													business.isContacted,
												lead: business?.lead,
												note: business?.note ?? '',
												userAddedData:
													business.userAddedData
											} as UserNotes
										);
									}}
								>
									<Website className="business-preview-email-modal-link-icon" />
									<span>
										{business.website.startsWith(
											'http://'
										) ||
										business.website.startsWith('https://')
											? business.website
											: `http://${business.website}`}
									</span>
								</a>
							</div>
						)}
					{business?.facebook?.website &&
						business.facebook?.website !== 'false' &&
						business.facebook?.website !== false && (
							<div className="business-preview-email-modal-link-row">
								<a
									rel="noreferrer"
									href={encodeUrl(
										business.facebook.website.startsWith(
											'http://'
										) ||
											business.facebook.website.startsWith(
												'https://'
											)
											? business.facebook.website
											: `http://${business.facebook.website}`
									)}
									className="business-preview-email-modal-link"
									target="_blank"
									onClick={() => {
										addLinkAnalytics('WEBSITE');
									}}
								>
									<Website className="business-preview-email-modal-link-icon" />
									<span>
										{business.facebook.website.startsWith(
											'http://'
										) ||
										business.facebook.website.startsWith(
											'https://'
										)
											? business.facebook.website
											: `http://${business.facebook.website}`}
									</span>
								</a>
							</div>
						)}
					{business && (
						<div className="business-preview-email-modal-link-row">
							<a
								rel="noreferrer"
								href={encodeUrl(
									`https://google.com/search?q=${searchTerm()}`
								)}
								className="business-preview-email-modal-link"
								target="_blank"
								onClick={() => {
									addLinkAnalytics('WEBSITE');
									setBusinessNotes(
										business.id || (business._id as string),
										{
											contactType: {
												google: true
											},
											isContacted: business.isContacted,
											lead: business?.lead,
											note: business?.note ?? '',
											userAddedData:
												business.userAddedData
										} as UserNotes
									);
								}}
							>
								<Google className="business-preview-email-modal-link-icon" />
								<span>Google Search</span>
							</a>
						</div>
					)}
					{business?.userAddedData?.map((data, ind) => {
						if (data?.type === 'web-link') {
							return (
								<div
									className="business-preview-email-modal-link-row"
									key={data.data}
								>
									<a
										rel="noreferrer"
										href={encodeUrl(
											data?.linkData.indexOf('http') ===
												-1 ||
												data?.linkData.indexOf(
													'https'
												) === -1
												? `https://${data?.linkData}`
												: data?.linkData
										)}
										className="business-preview-email-modal-link"
										target="_blank"
										onClick={() => {
											addLinkAnalytics('WEBSITE');
										}}
									>
										<Website className="business-preview-email-modal-link-icon" />
										<span>
											{data.data.startsWith('http://') ||
											data.data.startsWith('https://')
												? data.data
												: `http://${data.data}`}
										</span>
									</a>
									{!noEdit && (
										<Trash
											className="info-tab-site-trash"
											onClick={() => {
												openDeleteDataModal(ind);
											}}
										/>
									)}
								</div>
							);
						}
					})}
					{business?.yelp?.url && (
						<div className="business-preview-email-modal-link-row">
							<a
								rel="noreferrer"
								href={business.yelp.url}
								target="_blank"
								className="business-preview-email-modal-link"
								onClick={() => {
									addLinkAnalytics('YELP');
									setBusinessNotes(
										business.id || (business._id as string),
										{
											contactType: {
												yelp: true
											},
											isContacted: business.isContacted,
											lead: business?.lead,
											note: business?.note ?? '',
											userAddedData:
												business.userAddedData
										} as UserNotes
									);
								}}
							>
								<Yelp className="business-preview-email-modal-link-icon" />
								<span>Yelp</span>
							</a>
						</div>
					)}
					{business?.facebook?.url && (
						<div className="business-preview-email-modal-link-row">
							<a
								href={business.facebook.url}
								rel="noreferrer"
								target="_blank"
								className="business-preview-email-modal-link"
								onClick={() => {
									addLinkAnalytics('FACEBOOKWEBSITE');
									setBusinessNotes(
										business.id || (business._id as string),
										{
											contactType: {
												facebook: true
											},
											isContacted: business.isContacted,
											lead: business?.lead,
											note: business?.note ?? '',
											userAddedData:
												business.userAddedData
										} as UserNotes
									);
								}}
							>
								<Messenger className="business-preview-email-modal-link-icon business-preview-email-modal-link-icon-messenger" />
								<span>Find on Facebook</span>
							</a>
						</div>
					)}
					{business?.address?.city && (
						<div className="business-preview-email-modal-link-row instagram">
							<a
								rel="noreferrer"
								target="_blank"
								href={`https://www.google.com/search?q=${encodeURIComponent(
									business.address?.city as string
								)}+${encodeURIComponent(
									business.name as string
								)}+instagram
                  `}
								className="business-preview-email-modal-link-instagram"
								onClick={() => {
									addLinkAnalytics('INSTAGRAM');
									setBusinessNotes(
										business.id || (business._id as string),
										{
											contactType: {
												instagram: true
											},
											isContacted: business.isContacted,
											lead: business?.lead,
											note: business?.note ?? '',
											userAddedData:
												business.userAddedData
										} as UserNotes
									);
								}}
							>
								<Instagram className="business-preview-email-modal-link-row-icon business-preview-email-modal-link-icon-instagram" />
								<p>Find on Instagram</p>
							</a>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default BusinessPreviewEmailModal;
